import Axios, { AxiosRequestConfig } from 'axios';
import ErrorResponseHandler from '../services/ErrorResponseHandler';
import SessionStorageService from '../services/SessionStorageService';
import UserIdentityService from '../services/UserIdentityService';
import GlobalConfig from './GlobalConfig';

const request = (
    isJsonRequest: boolean = true
): AxiosRequestConfig => {
    const AuthHeader = 'Authorization';
    const LanugageCodeHeader = 'LanguageCode';
    
    const getCurrentLocale = () => {
        const currentLanguage = SessionStorageService.get('selectedLanguage');

        if (currentLanguage) {
            return currentLanguage;
        }
        
        return '';
    }
        
    const getBearerToken = () => {
        return UserIdentityService.getAuthorizationHeaderValue();
    }

    return {
        baseURL: GlobalConfig.val().GatewayApiUrl,
        headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
        },
        transformRequest: (data: any, headers: any) => {
            const token = getBearerToken();
            const currentLanguageCode = getCurrentLocale();
    
            if (token !== '') {
                headers[AuthHeader] = token;
            }
    
            if (!!currentLanguageCode.length) {
                headers[LanugageCodeHeader] = currentLanguageCode;
            }
    
            if (isJsonRequest) {
                return JSON.stringify(data);
            }
            return data;
        }
    }
}

const get = async <T extends {}> (url: string): Promise<T> => {
    const config = request();

    try {
        const response = await Axios.get<T>(url, config);

        if (response.status >= 200 && response.status <= 300) {
            return Promise.resolve(response.data)
        }

        return Promise.reject(response);
    } catch (error) {
        if (Axios.isCancel(error)) {
            // handle error after canceling request
        } else {
            ErrorResponseHandler.defaultHandler(error);
        }

        return Promise.reject(error);
    }
}

const post = async <T extends {}> (
    url: string, 
    data?: any, 
    customHeaders?: any, 
    isJsonRequest: boolean = true, 
    customConfigs: any = null, 
    forbiddenHandled?: boolean
): Promise<T> => {
    let config = request(isJsonRequest);

    if (customConfigs) {
        config = {...config, ...customConfigs};
    }

    if (customHeaders !== undefined && customHeaders !== null) {
        config.headers = customHeaders;
    }

    try {
        const response = await Axios.post<T>(url, data, config);
        if (response.status >= 200 && response.status <= 300) {
            return Promise.resolve(response.data as T)
        }
        return Promise.reject(response);
    }
    catch (error) {
        ErrorResponseHandler.defaultHandler(error, forbiddenHandled);

        return Promise.reject(error);
    }
}

const put = async <T extends {}> (
    url: string, 
    data?: any, 
    customHeaders?: any, 
    isJsonRequest: boolean = true, 
    forbiddenHandled?: boolean
): Promise<T> => {
    let config = request(isJsonRequest);

    if (customHeaders !== undefined) {
        config.headers = customHeaders;
    }

    try {
        const response = await Axios.put<T>(url, data, config);
        if (response.status >= 200 && response.status <= 300) {
            return Promise.resolve(response.data as T)
        }

        return Promise.reject(response);
    }
    catch (error) {
        ErrorResponseHandler.defaultHandler(error, forbiddenHandled);
        
        return Promise.reject(error);
    }
}

const del = async <T extends {}> (
    url: string, 
    customHeaders?: any, 
    isJsonRequest: boolean = true, 
    forbiddenHandled?: boolean
): Promise<T> => {
    let config = request(isJsonRequest);

    if (customHeaders !== undefined) {
        config.headers = customHeaders;
    }

    try {
        const response = await Axios.delete(url, config);
        if (response.status >= 200 && response.status <= 300) {
            return Promise.resolve(response.data as T);
        }

        return Promise.reject(response);
    }
    catch (error) {
        ErrorResponseHandler.defaultHandler(error, forbiddenHandled);

        return Promise.reject(error);
    }
}

export default {
    get,
    post,
    put,
    del
}